<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Eye, RotateCcw, Search } from '@lucide/vue'
import { Badge, Button, EmptyState, Input, PageHeader, Pagination, Section, Spinner, Table, Toolbar } from '@drama/ui'

import { listNovelRuns } from '../../api/novel/runs.js'
import NovelPackPicker from '../../components/novel/NovelPackPicker.vue'
import { useNovelPageText } from '../../components/novel/novel-page-text.js'

const NS = 'visualGameAdmin.novelRuns.list'

const { t } = useI18n()
const router = useRouter()
const { apiError, dateTime } = useNovelPageText()
const tr = (key, params) => t(`${NS}.${key}`, params ?? {})

const loading = ref(false)
const error = ref(null)
const items = ref([])
const total = ref(0)
const page = ref(1)
const pages = ref(1)
const pageSize = ref(20)
// pack 是作品选择器选中的 { id, title, slug }，null 表示全部作品。
const filters = reactive({ email: '', pack: null, failed_only: false })

const columns = computed(() => [
  { key: 'user_email', label: tr('table.player'), width: '240px' },
  { key: 'pack_title', label: tr('table.pack'), width: '220px' },
  { key: 'status', label: tr('table.status'), width: '100px' },
  { key: 'turn_count', label: tr('table.turns'), width: '80px', align: 'right' },
  { key: 'has_pending_jobs', label: tr('table.jobs'), width: '110px' },
  { key: 'failed_jobs_count', label: tr('table.failedJobs'), width: '100px', align: 'right' },
  { key: 'last_played_at', label: tr('table.lastPlayed'), width: '170px' },
  { key: 'actions', label: tr('table.actions'), width: '110px', align: 'right', fixed: 'right' },
])

const summary = computed(() => tr('pager.summary', { total: total.value, page: page.value, pages: pages.value }))

onMounted(load)

async function load() {
  loading.value = true
  error.value = null
  try {
    const data = await listNovelRuns({
      email: filters.email.trim() || undefined,
      pack_id: filters.pack?.id,
      failed_only: filters.failed_only ? 'true' : undefined,
      page: page.value,
      page_size: pageSize.value,
    })
    items.value = data.items
    total.value = data.total
    pages.value = data.pages
    page.value = Math.min(data.page, data.pages)
  } catch (requestError) {
    error.value = apiError(requestError)
  } finally {
    loading.value = false
  }
}

function applyFilters() { page.value = 1; load() }
function resetFilters() { filters.email = ''; filters.pack = null; filters.failed_only = false; applyFilters() }
function selectPack(pack) { filters.pack = pack; applyFilters() }
function setFailedOnly(checked) { filters.failed_only = checked; applyFilters() }
function prevPage() { page.value -= 1; load() }
function nextPage() { page.value += 1; load() }
function changePageSize(size) { pageSize.value = size; applyFilters() }

function openAudit(row) {
  router.push({ name: 'novel-run-audit', params: { runId: row.id } })
}
</script>

<template>
  <div class="nr vga-page" data-insp-path="apps/visual-game-admin/src/pages/novel/Runs.vue">
    <PageHeader :title="t('visualGameAdmin.novelRuns.title')" />

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ tr('filters.email') }}</label>
          <Input v-model="filters.email" type="search" size="sm" :placeholder="tr('filters.emailPlaceholder')" @keyup.enter="applyFilters" />
        </div>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ tr('filters.pack') }}</label>
          <NovelPackPicker
            :model-value="filters.pack"
            :label="tr('filters.pack')" :placeholder="tr('filters.packSearch')" :empty-text="tr('filters.packEmpty')"
            :loading-text="tr('filters.packSearching')" :clear-label="tr('filters.packClear')"
            @update:model-value="selectPack"
          />
        </div>
        <div class="drama-toolbar-field">
          <label class="nr__check">
            <input type="checkbox" :checked="filters.failed_only" data-testid="novel-runs-failed-only" @change="setFailedOnly($event.target.checked)" />
            {{ tr('filters.failedOnly') }}
          </label>
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" /> {{ tr('filters.apply') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error" :title="error.title">{{ error.text }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <EmptyState v-if="items.length === 0" :title="tr('empty')" />
        <Table v-else :columns="columns" :rows="items" clickable @row-click="openAudit">
          <template #user_email="{ row }">{{ row.user_email || tr('unknownUser') }}</template>
          <template #pack_title="{ row }">
            <div v-if="row.pack_id" class="nr__pack">
              <button type="button" class="nr__link" :title="tr('filterByPack')" @click.stop="selectPack({ id: row.pack_id, title: row.pack_title, slug: row.pack_slug })">
                {{ row.pack_title || row.pack_id }}
              </button>
              <span class="nr__muted nr__slug">{{ row.pack_slug }}</span>
            </div>
            <span v-else>—</span>
          </template>
          <template #status="{ row }">
            <Badge size="sm" :variant="row.status === 'active' ? 'success' : 'neutral'">{{ tr(`statuses.${row.status}`) }}</Badge>
          </template>
          <template #has_pending_jobs="{ row }">
            <Badge v-if="row.has_pending_jobs" size="sm" variant="warning">{{ tr('jobsPending') }}</Badge>
            <span v-else class="nr__muted">{{ tr('jobsIdle') }}</span>
          </template>
          <template #failed_jobs_count="{ row }">
            <Badge v-if="row.failed_jobs_count > 0" size="sm" variant="danger" :data-testid="`novel-run-failed-${row.id}`">{{ row.failed_jobs_count }}</Badge>
            <span v-else class="nr__muted">0</span>
          </template>
          <template #last_played_at="{ row }">{{ dateTime(row.last_played_at) }}</template>
          <template #actions="{ row }">
            <Button variant="secondary" size="sm" @click.stop="openAudit(row)">
              <Eye :size="14" /> {{ tr('audit') }}
            </Button>
          </template>
        </Table>
      </Section>
      <Pagination
        :has-prev="page > 1" :has-next="page < pages" :loading="loading" :summary="summary"
        :prev-label="tr('pager.prev')" :next-label="tr('pager.next')"
        :page-size="pageSize" :page-size-options="[20, 50, 100]" :page-size-label="tr('pager.pageSize')"
        @prev="prevPage" @next="nextPage" @update:page-size="changePageSize"
      />
    </template>
  </div>
</template>

<style scoped>
.nr__pack { display: grid; gap: 2px; justify-items: start; min-width: 0; }
.nr__link { padding: 0; border: none; background: none; color: var(--drama-color-primary, inherit); font: inherit; text-align: left; cursor: pointer; }
.nr__link:hover { text-decoration: underline; }
.nr__muted { color: var(--drama-color-text-muted); }
.nr__slug { font-size: var(--drama-font-size-xs, 12px); }
.nr__check { display: inline-flex; align-items: center; gap: var(--drama-space-xs); cursor: pointer; }
</style>
